import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-24 md:pt-32 pb-24 bg-white">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <span className="text-neutral-400 uppercase tracking-[0.2em] text-sm">
            Get In Touch
          </span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mt-2 text-black">
            Contact Us
          </h1>
          <p className="mt-6 text-neutral-500 text-lg max-w-2xl mx-auto">
            Have questions about our pieces or need custom work? We'd love to hear from you.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name *"
                  required
                  data-testid="contact-name"
                  className="input-underline"
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email address *"
                  required
                  data-testid="contact-email"
                  className="input-underline"
                />
              </div>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Subject *"
                required
                data-testid="contact-subject"
                className="input-underline"
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your message *"
                required
                rows={6}
                data-testid="contact-message"
                className="input-underline resize-none"
              />

              {status === 'success' && (
                <p className="text-green-600">Message sent successfully! We'll get back to you soon.</p>
              )}
              {status === 'error' && (
                <p className="text-red-500">Something went wrong. Please try again.</p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-primary flex items-center gap-2"
              >
                {loading ? (
                  <div className="spinner w-5 h-5" />
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-12"
          >
            <div>
              <h3 className="font-semibold uppercase tracking-wider text-sm mb-6">
                Contact Information
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Mail size={20} className="text-neutral-400 mt-1" />
                  <div>
                    <p className="text-neutral-400 text-sm uppercase tracking-wider">Email</p>
                    <p className="mt-1">hello@tenome.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin size={20} className="text-neutral-400 mt-1" />
                  <div>
                    <p className="text-neutral-400 text-sm uppercase tracking-wider">Location</p>
                    <p className="mt-1">Northampton, United Kingdom</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={20} className="text-neutral-400 mt-1" />
                  <div>
                    <p className="text-neutral-400 text-sm uppercase tracking-wider">Phone</p>
                    <p className="mt-1">07786 025399</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 bg-neutral-50 border border-neutral-200">
              <h3 className="font-semibold uppercase tracking-wider text-sm mb-4">
                Custom Orders
              </h3>
              <p className="text-neutral-500 leading-relaxed">
                Looking for a custom piece or bulk order? We offer bespoke sculptures 
                for collectors and businesses. Get in touch to discuss your project.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
